import { useEffect, useState, useRef } from 'react';
import '../styles/HomeStyle.css';
import Paho from 'paho-mqtt';
import { useSnackbar, closeSnackbar } from 'notistack';
import { sendMqttLog, createMqttLogMessage } from '../utils/mqttLogger';

const useConnectionMQTT = (props) => {
  const {
    data, lights, setLights, conditioner, setСonditioner, sensors,
    setSensors,
  } = props;
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const maxReconnectAttempts = 10;
  const reconnectTimeoutRef = useRef(null);
  const connectionCheckIntervalRef = useRef(null);
  const setConnectionRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();

  const driver = data.drivers?.[0];

  const {
    ip, port, login, password,
  } = driver;

  const [client] = useState(new Paho.Client(
    String(ip),
    Number(port),
    `mqtt-async-test-${parseInt(Math.round(Math.random(100000000, 1000000000) * 1000000000), 10)}`,
  ));

  const showNotify = (message, variant, persist) => {
    enqueueSnackbar(message, { persist, variant });
  };

  const handleSendMqttLog = async (message) => {
    await sendMqttLog(message, data, { lights, conditioner, sensors });
  };

  const clearTimers = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (connectionCheckIntervalRef.current) {
      clearInterval(connectionCheckIntervalRef.current);
      connectionCheckIntervalRef.current = null;
    }
  };

  const subscribeToTopics = () => {
    lights?.forEach((item) => {
      item?.capabilities?.forEach((subItem) => {
        subItem?.feedback_address.forEach((fb) => {
          try {
            client.subscribe(fb);
          } catch (e) {
            console.error('Subscribe error:', e);
          }
        });
      });
    });
    conditioner?.forEach((item) => {
      item?.capabilities?.forEach((subItem) => {
        subItem?.feedback_address.forEach((fb) => {
          try {
            client.subscribe(fb);
          } catch (e) {
            console.error('Subscribe error:', e);
          }
        });
      });
    });
    sensors?.forEach((item) => {
      item?.capabilities?.forEach((subItem) => {
        subItem?.feedback_address.forEach((fb) => {
          try {
            client.subscribe(fb);
          } catch (e) {
            console.error('Subscribe error:', e);
          }
        });
      });
    });
  };

  const checkConnection = () => {
    if (!client.isConnected() && isConnected) {
      console.log('Connection lost, attempting to reconnect...');
      setIsConnected(false);
      if (setConnectionRef.current) {
        setConnectionRef.current();
      }
    }
  };

  const onMessage = (message) => {
    setLights((prevState) => {
      const newState = prevState.map((item) => {
        const newSubItem = item?.capabilities?.map((subItem) => {
          if (subItem?.feedback_address[0] === message.destinationName) {
            return { ...subItem, fb: parseInt(message.payloadString, 10) };
          }
          return subItem;
        });
        return { ...item, capabilities: newSubItem };
      });
      return newState;
    });
    setСonditioner((prevState) => {
      const newState = prevState.map((item) => {
        const newSubItem = item?.capabilities?.map((subItem) => {
          if (subItem?.feedback_address.length > 1) {
            const isDestination = subItem?.feedback_address?.find?.((val) => val === message.destinationName);
            if (isDestination) {
              const id = isDestination.slice(isDestination.length - 3, isDestination.length);
              return { ...subItem, [`fb-${id}`]: parseInt(message.payloadString, 10) };
            }
            return subItem;
          }
          if (subItem?.feedback_address[0] === message.destinationName) {
            return { ...subItem, fb: parseFloat(message.payloadString, 10) };
          }
          return subItem;
        });
        return { ...item, capabilities: newSubItem };
      });
      return newState;
    });
    setSensors((prevState) => {
      const newState = prevState.map((item) => {
        const newSubItem = item?.capabilities?.map((subItem) => {
          if (subItem?.feedback_address[0] === message.destinationName) {
            return { ...subItem, fb: parseFloat(message.payloadString, 10) };
          }
          return subItem;
        });
        return { ...item, capabilities: newSubItem };
      });
      return newState;
    });
    if (!isConnected) setIsConnected(true);
    setLoading(false);
  };

  const setConnection = () => {
    clearTimers();

    if (reconnectAttempts >= maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      showNotify('Превышено количество попыток подключения', 'error', true);
      return;
    }

    const onConnectionLost = (responseObject) => {
      console.log('onConnectionLost!', responseObject);
      console.log(`onConnectionLost:${responseObject.errorMessage}`);
      setIsConnected(false);
      showNotify('Восстанавливаем соединение', 'error', true);

      const delay = Math.min(1000 * (2 ** reconnectAttempts), 30000);
      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectAttempts((prev) => prev + 1);
        setConnection();
      }, delay);
    };

    const options = {
      timeout: 4,
      reconnect: false,
      useSSL: true,
      cleanSession: true,
    };

    if (login) options.userName = String(login);
    if (password) options.password = String(password);

    client.connect({
      ...options,
      onSuccess: () => {
        console.log('Connected!');
        setIsConnected(true);
        setReconnectAttempts(0);
        clearTimers();

        closeSnackbar();
        showNotify('Соединение установлено!', 'success');

        subscribeToTopics();

        connectionCheckIntervalRef.current = setInterval(checkConnection, 10000);
      },
      onFailure: () => {
        setIsConnected(false);
        setLoading(false);
        console.log('Failed to connect!');

        const delay = Math.min(1000 * (2 ** reconnectAttempts), 30000);
        reconnectTimeoutRef.current = setTimeout(() => {
          setReconnectAttempts((prev) => prev + 1);
          setConnection();
        }, delay);
      },
    });
    client.onMessageArrived = onMessage;
    client.onConnectionLost = onConnectionLost;
  };

  setConnectionRef.current = setConnection;

  // Обработчик изменения видимости страницы
  const handleVisibilityChange = () => {
    if (!document.hidden && !isConnected) {
      console.log('Page became visible, checking connection...');
      checkConnection();
    }
  };

  // Обработчик фокуса окна
  const handleWindowFocus = () => {
    if (!isConnected) {
      console.log('Window focused, checking connection...');
      checkConnection();
    }
  };

  // Обработчик онлайн/офлайн
  const handleOnline = () => {
    console.log('Network is online, checking connection...');
    if (!isConnected) {
      checkConnection();
    }
  };

  const changeValue = (addresses, value, qos, isScenario = false) => {
    addresses.forEach((address) => {
      const message = new Paho.Message(value.toString());
      message.destinationName = address;
      message.qos = qos || 0;

      const logMessage = createMqttLogMessage(address, value, qos, isScenario, 'sent');
      handleSendMqttLog(logMessage);

      try {
        client.send(message);
      } catch (e) {
        console.error('e', e);
        if (!client.isConnected()) {
          checkConnection();
        }
      }
    });
  };

  useEffect(() => {
    try {
      setConnection();

      // Добавляем слушатели событий
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleWindowFocus);
      window.addEventListener('online', handleOnline);
    } catch (e) {
      console.error('e', e);
      setIsConnected(false);
    }

    // Очистка при размонтировании
    return () => {
      clearTimers();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('online', handleOnline);

      if (client.isConnected()) {
        client.disconnect();
      }
    };
  }, []);

  return { changeValue, loading, isConnected };
};

export default useConnectionMQTT;
